import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { BarChart3, Phone, Clock, TrendingUp, Plus, Trash2, Play, Activity, FileText } from "lucide-react";
import { toast } from "sonner";
import TranscriptViewer from "@/components/TranscriptViewer";
import IntentHeatmap from "@/components/analytics/IntentHeatmap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const langLabels = { en: "English", es: "Spanish", fr: "French", ht: "Haitian Creole" };
const sentimentColors = { positive: "#00C853", neutral: "#FFB800", negative: "#FF2A2A" };
const PIE_COLORS = ["#002FA7", "#0A0A0A", "#00C853", "#FFB800"];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-5 flex items-start gap-4">
      <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: accent }}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-black text-[#0A0A0A] tracking-tight">{value}</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [sentimentText, setSentimentText] = useState("");
  const [sentimentResult, setSentimentResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const load = async () => {
    try {
      const [a, r, w] = await Promise.all([
        ax.get(`${API}/client/analytics`),
        ax.get(`${API}/client/recordings`),
        ax.get(`${API}/client/webhooks`),
      ]);
      setAnalytics(a.data);
      setRecordings(r.data);
      setWebhooks(w.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const addWebhook = async () => {
    if (!newWebhookUrl) return;
    try {
      await ax.post(`${API}/client/webhooks`, { url: newWebhookUrl, events: ["call.completed", "call.started"] });
      toast.success("Webhook added");
      setNewWebhookUrl("");
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteWebhook = async (id) => {
    try {
      await ax.delete(`${API}/client/webhooks/${id}`);
      toast.success("Webhook deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const analyzeSentiment = async () => {
    if (!sentimentText) return;
    setAnalyzing(true);
    try {
      const res = await ax.post(`${API}/ai/sentiment`, { text: sentimentText });
      setSentimentResult(res.data);
    } catch { toast.error("Analysis failed"); }
    setAnalyzing(false);
  };

  const sentimentData = analytics ? Object.entries(analytics.sentiment_distribution || {}).map(([k, v]) => ({ name: t(`client.${k}`), value: v, fill: sentimentColors[k] })) : [];
  const langData = analytics ? Object.entries(analytics.language_distribution || {}).map(([k, v], i) => ({ name: langLabels[k] || k, value: v, fill: PIE_COLORS[i % PIE_COLORS.length] })) : [];

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight mb-6" data-testid="client-title">
            {t("client.title")}
          </h2>

          {/* Stats */}
          {analytics && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Phone} label={t("client.total_calls")} value={analytics.total_calls_today} accent="#002FA7" />
              <StatCard icon={TrendingUp} label={t("client.satisfaction")} value={`${analytics.avg_satisfaction}/5`} accent="#00C853" />
              <StatCard icon={Clock} label={t("client.handle_time")} value={`${analytics.avg_handle_time}m`} accent="#FFB800" />
              <StatCard icon={BarChart3} label={t("client.sla")} value={`${analytics.avg_sla_compliance}%`} accent="#0A0A0A" />
            </div>
          )}

          <Tabs defaultValue="analytics" className="space-y-4">
            <TabsList className="bg-white border border-[#E5E7EB] rounded-none h-10 p-0.5">
              <TabsTrigger value="analytics" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-analytics">{t("nav.analytics")}</TabsTrigger>
              <TabsTrigger value="intent" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-intent">Intent Heatmap</TabsTrigger>
              <TabsTrigger value="recordings" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-recordings">{t("nav.recordings")}</TabsTrigger>
              <TabsTrigger value="webhooks" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-webhooks">{t("nav.webhooks")}</TabsTrigger>
              <TabsTrigger value="sentiment" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-sentiment">{t("ai.sentiment_title")}</TabsTrigger>
            </TabsList>

            {/* Analytics */}
            <TabsContent value="analytics">
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-[#E5E7EB] bg-white p-6" data-testid="sentiment-chart">
                  <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A] mb-4">{t("client.sentiment_dist")}</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={sentimentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="value" radius={0}>
                        {sentimentData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="border border-[#E5E7EB] bg-white p-6" data-testid="language-chart">
                  <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A] mb-4">{t("client.language")}</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={langData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {langData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>

            {/* Intent Heatmap */}
            <TabsContent value="intent">
              <IntentHeatmap t={t} />
            </TabsContent>

            {/* Recordings */}
            <TabsContent value="recordings">
              <div className="border border-[#E5E7EB] bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-[#0A0A0A]/80">
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">Operator</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.language")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.duration")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.sentiment")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.satisfaction")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("call_queue.transcript")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recordings.map((r) => (
                      <TableRow key={r.recording_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`recording-${r.recording_id}`}>
                        <TableCell className="py-3 text-sm font-medium">{r.operator_name}</TableCell>
                        <TableCell className="py-3">
                          <Badge variant="outline" className="rounded-none text-[10px] font-bold uppercase">{r.caller_language}</Badge>
                        </TableCell>
                        <TableCell className="py-3 text-sm">{Math.floor(r.duration_seconds / 60)}:{String(r.duration_seconds % 60).padStart(2, "0")}</TableCell>
                        <TableCell className="py-3">
                          <span className="text-xs font-bold" style={{ color: sentimentColors[r.sentiment] }}>{r.sentiment}</span>
                        </TableCell>
                        <TableCell className="py-3 text-sm font-bold">{r.satisfaction_score}/5</TableCell>
                        <TableCell className="py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none h-7 text-[10px] font-bold"
                            onClick={async () => {
                              try {
                                const res = await ax.get(`${API}/calls/transcript/${r.recording_id}`);
                                setTranscript(res.data);
                                setTranscriptOpen(true);
                              } catch { toast.error("Transcript not available"); }
                            }}
                            data-testid={`transcript-btn-${r.recording_id}`}
                          >
                            <FileText size={12} className="mr-1" /> {t("call_queue.transcript")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Transcript Dialog */}
              <Dialog open={transcriptOpen} onOpenChange={setTranscriptOpen}>
                <DialogContent className="rounded-none max-w-2xl">
                  <DialogHeader><DialogTitle>{t("call_queue.transcript")}</DialogTitle></DialogHeader>
                  <TranscriptViewer transcript={transcript} />
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Webhooks */}
            <TabsContent value="webhooks" className="space-y-4">
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="webhook-config">
                <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A] mb-4">{t("client.webhook_title")}</h3>
                <div className="flex gap-2 mb-4">
                  <Input className="rounded-none flex-1" placeholder={t("client.webhook_url")} value={newWebhookUrl} onChange={(e) => setNewWebhookUrl(e.target.value)} data-testid="webhook-url-input" />
                  <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={addWebhook} data-testid="add-webhook-btn">
                    <Plus size={14} className="mr-1" /> {t("client.add_webhook")}
                  </Button>
                </div>
                <div className="space-y-2">
                  {webhooks.map((w) => (
                    <div key={w.webhook_id} className="flex items-center justify-between p-3 bg-[#F8F9FA] border border-[#E5E7EB]" data-testid={`webhook-${w.webhook_id}`}>
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A] font-mono">{w.url}</p>
                        <div className="flex gap-1 mt-1">
                          {w.events?.map((e) => (
                            <span key={e} className="text-[9px] bg-[#002FA7]/5 text-[#002FA7] px-1.5 py-0.5 font-bold">{e}</span>
                          ))}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-[#FF2A2A] hover:bg-[#FF2A2A]/5" onClick={() => deleteWebhook(w.webhook_id)} data-testid={`delete-webhook-${w.webhook_id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  {webhooks.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-4">{t("common.no_data")}</p>}
                </div>
              </div>
            </TabsContent>

            {/* Sentiment Analysis */}
            <TabsContent value="sentiment" className="space-y-4">
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="sentiment-analysis">
                <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A] mb-4">{t("ai.sentiment_title")}</h3>
                <Textarea className="rounded-none mb-3 h-24" placeholder={t("ai.enter_text")} value={sentimentText} onChange={(e) => setSentimentText(e.target.value)} data-testid="sentiment-input" />
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase mb-3" onClick={analyzeSentiment} disabled={analyzing} data-testid="analyze-sentiment-btn">
                  {analyzing ? t("common.loading") : t("ai.analyze")}
                </Button>
                {sentimentResult && (
                  <div className="bg-[#F8F9FA] border border-[#E5E7EB] p-4" data-testid="sentiment-result">
                    <div className="flex items-center gap-3 mb-2">
                      <Activity size={18} style={{ color: sentimentColors[sentimentResult.sentiment] || "#9CA3AF" }} />
                      <span className="text-lg font-bold capitalize" style={{ color: sentimentColors[sentimentResult.sentiment] || "#0A0A0A" }}>{sentimentResult.sentiment}</span>
                      <span className="text-xs text-[#9CA3AF]">({Math.round((sentimentResult.confidence || 0) * 100)}% confidence)</span>
                    </div>
                    <p className="text-sm text-[#4B5563]">{sentimentResult.summary}</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
